import React from 'react';
import ClassNames from 'classnames';
import PropTypes from 'prop-types';
import Icon from '@welldev-group/rcl-icon';
import IconButton from '@welldev-group/rcl-icon-button';
import { Avatar, I18nText, LabelHint } from '@welldev-group/react-comp-lib';
import humanizeDuration from 'humanize-duration';
import SanitizedHTML from '../../../shared/components/SanitizedHTML';
import httpClient from '../../../shared/libraries/httpClient';
import routes from '../../../shared/routes';
import addLazyLoadingToImages from '../../../shared/helper/addLazyLoadingToImages';

const MessageCard = props => {
  const {
    message,
    setMessageToEdit,
    setMessageToEditId,
    selectedSupportTicketId,
    ticketCreatedBy,
    fetchMessages,
    signedInAsSuperAdmin,
  } = props;

  const isScheduledMessage =
    message.state === 'scheduled' && signedInAsSuperAdmin;

  const isInternalMessage = message.internal;

  const editMessage = () => {
    setMessageToEdit(message.body);
    setMessageToEditId(message.id);
  };

  const deleteMessage = async () => {
    const { data } = await httpClient.delete(
      routes.support.message.delete({
        support_ticket_id: selectedSupportTicketId,
        id: message.id,
      }),
    );

    if (data.error === null) {
      fetchMessages();
    }
  };

  const humanReadableTimeStamp = () => {
    const currentTime = new Date();
    const messageCreationTime = new Date(message.createdAt);
    const humanizedDuration = humanizeDuration(
      currentTime - messageCreationTime,
      { largest: 1 },
    );

    return I18n.t('components.support.humanized_duration', {
      duration: humanizedDuration,
    });
  };

  const showMailerInfo = () => {
    let info = null;
    if (message.emailSent) {
      info = I18n.t('components.support.email_sent');
    } else if (message.emailSendsIn) {
      info = I18n.t('components.support.email_sends_in', {
        time: Math.ceil(message.emailSendsIn),
      });
    }

    return info;
  };

  return (
    <div
      className={ClassNames(
        'support-message-card',
        {
          'support-message-card--align-right':
            message.user.loggedIn && !isInternalMessage,
        },
        {
          'support-message-card--stretch': isInternalMessage,
        },
      )}
    >
      <div className="support-message-card__user-info">
        <div className="support-message-card__user-info-left">
          <div className="support-message-card__user-info-wrapper">
            <span>
              <Avatar
                src={message.user.avatar}
                firstName={message.user.firstName}
                lastName={message.user.lastName}
                size="tiny"
              />
            </span>
            <span>{message.user.name}</span>
          </div>
          <div>
            <span className="created-by">
              {ticketCreatedBy &&
                ticketCreatedBy.superAdmin &&
                I18n.t('components.support.ticket.created_by', {
                  name: ticketCreatedBy.name,
                })}
            </span>
          </div>
        </div>
        <div className="support-message-card__user-info-timestamp">
          {humanReadableTimeStamp()}
          <LabelHint
            placement="top-left"
            size="tiny"
            hint={new Date(message.createdAt).toUTCString()}
          />
        </div>
      </div>
      <div
        className={ClassNames(
          'support-message-card__message',
          {
            'support-message-card__message--scheduled': isScheduledMessage,
          },
          {
            'support-message-card__message--internal': isInternalMessage,
          },
        )}
      >
        <div className="support-message-card__message-content">
          {isInternalMessage && (
            <strong>
              <I18nText id="support.internal_message" />
            </strong>
          )}
          <SanitizedHTML html={addLazyLoadingToImages(message.body)} />
        </div>
        <div className="support-message-card__actions">
          <div>
            {(isScheduledMessage || isInternalMessage) && (
              <IconButton
                onClick={editMessage}
                isIconOnly={true}
                icon={<Icon name="edit" />}
                size="tiny"
              />
            )}
            {(isScheduledMessage || isInternalMessage) && (
              <IconButton
                onClick={deleteMessage}
                isIconOnly={true}
                icon={<Icon name="delete" />}
                size="tiny"
              />
            )}
          </div>
          <div>
            {message.seenByClient ? (
              <I18nText id="support.seen_by_client" />
            ) : (
              showMailerInfo()
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

MessageCard.propTypes = {
  fetchMessages: PropTypes.func.isRequired,
  setMessageToEdit: PropTypes.func.isRequired,
  setMessageToEditId: PropTypes.func.isRequired,
  selectedSupportTicketId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  signedInAsSuperAdmin: PropTypes.bool.isRequired,
  message: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    body: PropTypes.string,
    createdAt: PropTypes.string,
    state: PropTypes.string,
    internal: PropTypes.bool,
    emailSendsIn: PropTypes.string,
    seenByClient: PropTypes.bool,
    emailSent: PropTypes.bool,
    user: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      avatar: PropTypes.string,
      loggedIn: PropTypes.bool,
      name: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    }),
  }).isRequired,
  ticketCreatedBy: PropTypes.shape({
    name: PropTypes.string,
    superAdmin: PropTypes.bool,
  }),
};

MessageCard.defaultProps = {
  ticketCreatedBy: null,
};

export default MessageCard;
